'use client';

import { useState } from 'react';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { DiscountedPrice } from '@/components/Price/DiscountedPrice';
import {
  SprdResponsiveImage,
  SprdResponsiveImageData,
} from '@/components/ResponsiveImage/SprdResponsiveImage';
import { Link } from '@/i18n/navigation';

export interface TShirtOfTheWeekDiscountItem {
  sellableId: string;
  buttonLabel: string;
  discountAmount: number;
  price: number;
  designName: string;
  productTypeName: string;
  image: SprdResponsiveImageData;
}

interface TShirtOfTheWeekDiscountSwitcherProps {
  className?: string;
  imageBaseUrl: string;
  heading: string;
  items: TShirtOfTheWeekDiscountItem[];
}

export function TShirtOfTheWeekDiscountSwitcher({
  className,
  imageBaseUrl,
  heading,
  items,
}: Readonly<TShirtOfTheWeekDiscountSwitcherProps>) {
  const t = useTranslations();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selected = items[selectedIndex]!;
  const hasMultipleItems = items.length > 1;

  return (
    <div
      className={clsx(
        'mx-auto flex max-w-screen-xl flex-col flex-wrap justify-between gap-6 sm:flex-row md:gap-8 lg:gap-18 xl:gap-24.5',
        className
      )}
    >
      <div className="sm:flex-1">
        <SprdResponsiveImage
          imageBaseUrl={imageBaseUrl}
          image={selected.image}
        />
      </div>

      <div className="flex flex-col justify-center gap-6 sm:flex-1 md:pr-16">
        <h3 className="text-3xl leading-tight font-black uppercase md:text-4xl">{heading}</h3>

        <p className="text-sm leading-relaxed text-gray-800 md:text-base">
          {t('mpDetailPage.heading', {
            designName: selected.designName,
            productTypeName: selected.productTypeName,
          })}
        </p>

        <span className="text-2xl font-semibold">
          <DiscountedPrice
            price={selected.price}
            currentDiscountAmount={selected.discountAmount}
            excludePromoDiscount
          />
        </span>

        {hasMultipleItems && (
          <div className="flex flex-wrap gap-3">
            {items.map((item, index) => {
              const isSelected = index === selectedIndex;
              return (
                <button
                  key={item.sellableId}
                  type="button"
                  onClick={() => setSelectedIndex(index)}
                  aria-pressed={isSelected}
                  className={clsx(
                    'flex items-center justify-center border-2 border-black px-4 py-2 text-center text-sm font-semibold',
                    isSelected
                      ? 'bg-black text-white'
                      : 'hover:border-neutral-900 hover:bg-neutral-900 hover:text-white'
                  )}
                >
                  {item.buttonLabel}
                </button>
              );
            })}
          </div>
        )}

        <Link
          href={
            hasMultipleItems
              ? { pathname: '/shop/totw', query: { sellableId: selected.sellableId } }
              : { pathname: '/shop/totw' }
          }
          className="w-fit bg-black px-4 py-2 text-white hover:bg-gray-800"
        >
          {t('totwDiscount.cta')}
        </Link>
      </div>
    </div>
  );
}
